import { Container } from '@chakra-ui/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';

import { LDFlags } from '../../constants/flags';
import { DealType, useExternalCustomerUpdateMutation } from '../../gql/generated/graphql';
import { useFlag } from '../../hooks';
import { sameEmail } from '../../utils/customer';
import { DealStateEnum } from '../../utils/types/deal';
import { firstVisitAtom } from './RootDashboard';
import DashboardContent from './components/DashboardContent';
import TimeLine from './components/Timeline/Timeline';
import { devDealStateAtom, devDealTypeAtom, isDevActiveAtom } from './components/devToolbarAtoms';
import WelcomeModal from './components/welcomeModal/WelcomeModal';
import {
  DealStateMap,
  DealStateMapReverse,
  DealWithFarthestState,
  POST_SIGNING_STATES,
  UserDeal,
  getFarthestDealState,
} from './utils';

type BuyoutDashboardProps = {
  userDeal: UserDeal;
  loading: boolean;
};

// TODO: Rename this file to BuyoutDashboard.tsx once the marketplace dashboard
// work is merged and there is no longer a risk of merge conflicts.
const BuyoutDashboard = ({ userDeal, loading }: BuyoutDashboardProps) => {
  const postSigningStepperEnabled = useFlag(LDFlags.COM_POST_SIGNING_STEPPER_ENABLED);

  const [customerUpdateMutation] = useExternalCustomerUpdateMutation();
  const setFirstVisit = useSetAtom(firstVisitAtom);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  const [dealIsPending, setDealIsPending] = useState(true);

  // --- First visit / welcome modal ---
  useEffect(() => {
    const deal = userDeal;
    if (deal) {
      const isFirstVisit = deal.isCobuyer
        ? !deal.cobuyer?.dashboard_visited
        : !deal.customer?.dashboard_visited;

      const shouldShowModal = !!deal.isCobuyer && !sameEmail(deal.customer, deal.cobuyer);

      if (isFirstVisit) {
        if (shouldShowModal) {
          setShowWelcomeModal(true);
          setFirstVisit('pending');
        }
        const currentId = (deal.isCobuyer ? deal.cobuyer?.id : deal.customer?.id) || '';
        customerUpdateMutation({
          variables: { customerId: currentId, customer: { dashboard_visited: true } },
        });
      }
    }
  }, [userDeal?.id]);
  let farthestDealState = useMemo(() => {
    if (!userDeal?.deal_states) return DealStateEnum.SoftClose;

    const farState = getFarthestDealState(userDeal?.deal_states);
    setDealIsPending(farState === DealStateEnum.SoftClose);
    return farState;
  }, [userDeal?.deal_states]);
  let state = userDeal?.state;

  // Forces booted state in case Deal was previously in the finalized state.
  let isDealBooted = userDeal?.state === DealStateEnum.Booted || !!userDeal?.request_boot;
  let type = userDeal?.type;

  const isDevActive = useAtomValue(isDevActiveAtom);
  const devDealState = useAtomValue(devDealStateAtom);
  const devDealType = useAtomValue(devDealTypeAtom);

  if (isDevActive) {
    farthestDealState = DealStateMapReverse[DealStateMap[devDealState]];
    state = devDealState;
    isDealBooted = devDealState === DealStateEnum.Booted;
    type = devDealType;
  }

  let deal = userDeal as DealWithFarthestState;
  if (deal) {
    deal = { ...userDeal, farthestState: farthestDealState, state, type };
  }
  const isRefi = deal?.type === DealType.Refi;
  const isDealSoftClosing = isDevActive
    ? farthestDealState === DealStateEnum.SoftClose
    : dealIsPending;
  const isDealStructuring = deal?.farthestState === DealStateEnum.Structuring;
  const isDealClosing = deal?.farthestState === DealStateEnum.Closing;
  // check current state, not farthest
  const isDealClosed = deal?.state === DealStateEnum.Closed;
  const isDealSentForSignatures = deal?.state === DealStateEnum.SentForSignatures;

  const shouldShowTimeLine = (dealState: DealStateEnum) => {
    return DealStateMap[dealState] >= DealStateMap[DealStateEnum.Closed];
  };

  const showDashboardStepper =
    isDealSoftClosing ||
    isDealStructuring ||
    isDealClosing ||
    isDealClosed ||
    isDealSentForSignatures;

  const showPostSigningStepper =
    postSigningStepperEnabled && POST_SIGNING_STATES.includes(deal?.farthestState);

  const showTimeline =
    !isDealBooted &&
    shouldShowTimeLine(deal?.farthestState) &&
    !showDashboardStepper &&
    !showPostSigningStepper;

  return (
    <>
      {showTimeline && (
        <TimeLine loading={loading} farthestDealState={farthestDealState} isRefi={isRefi} />
      )}
      <Container maxW="container.xl" px="unset" my="20px" flexGrow={1}>
        <DashboardContent
          deal={deal}
          isDealSoftClosing={isDealSoftClosing}
          isDealClosing={isDealClosing}
          isDealStructuring={isDealStructuring}
          showDashboardStepper={showDashboardStepper}
          showPostSigningStepper={showPostSigningStepper}
        />
      </Container>
      <WelcomeModal
        deal={deal}
        isOpen={showWelcomeModal}
        onClose={() => setShowWelcomeModal(false)}
        onCloseComplete={() => setFirstVisit('done')}
      />
    </>
  );
};

export default BuyoutDashboard;
